import React from 'react';
import { RowModel, flexRender } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { Intake } from '@/entities/Courses';
import { NavigationConfig } from '@/shared/ui/AppTable/ui/AppTable/AppTable';
import { isInteractiveEventTarget } from '@/shared/lib/utils/isInteractiveEventTarget';
import { IntakeGroupRow } from '../IntakeGroupRow';
import styles from './IntakesTableBody.module.scss';

interface IntakesTableBodyProps<TData> {
  rows: RowModel<TData>['rows'];
  navigationConfig?: NavigationConfig<TData>[];
  onRowClick?: (row: TData) => void;
  onToggleIntake?: (intakeId: number) => void;
  expandedIntakes: Set<number>;
  courseId: number;
}

export const IntakesTableBody = <T extends Intake>({
  rows,
  navigationConfig,
  onRowClick,
  onToggleIntake,
  expandedIntakes,
  courseId,
}: IntakesTableBodyProps<T>) => {
  const getNavigationConfig = (columnId: string) =>
    navigationConfig?.find((config) => config.columnId === columnId);

  return (
    <tbody>
      {rows.map((row) => {
        const intake = row.original as Intake;
        const isExpanded = expandedIntakes.has(intake.id);
        const hasGroups = Boolean(intake.groups && intake.groups.length > 0);
        const isExpandable = hasGroups && Boolean(onToggleIntake);
        const isClickable = Boolean(onRowClick) || isExpandable;

        return (
          <React.Fragment key={row.id}>
            <tr
              className={cn(styles.row, isClickable && styles.clickable)}
              aria-expanded={isExpandable ? isExpanded : undefined}
              onClick={(event) => {
                if (isInteractiveEventTarget(event.target)) return;

                if (isExpandable) {
                  onToggleIntake?.(intake.id);
                  return;
                }

                onRowClick?.(row.original);
              }}
            >
              {row.getVisibleCells().map((cell) => {
                const navConfig = getNavigationConfig(cell.column.id);
                const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext());

                return (
                  <td
                    key={cell.id}
                    className={styles.cell}
                    onClick={(event) => {
                      if (isInteractiveEventTarget(event.target)) {
                        event.stopPropagation();
                      }
                    }}
                  >
                    {navConfig ? (
                      <Link to={navConfig.getUrl(row.original)} className={styles.navigationLink}>
                        {cellContent}
                      </Link>
                    ) : (
                      cellContent
                    )}
                  </td>
                );
              })}
            </tr>
            {isExpanded && hasGroups && (
              <>
                {intake.groups.map((group) => (
                  <IntakeGroupRow
                    key={`group-${intake.id}-${group.id}`}
                    group={group}
                    intakeId={intake.id}
                    courseId={courseId}
                  />
                ))}
              </>
            )}
          </React.Fragment>
        );
      })}
    </tbody>
  );
};
